import React, { useState, useEffect } from "react";
import {
  Edit2, Package, Shield, DollarSign, Calendar, Save,
  ChevronDown, ChevronUp, AlertCircle, ClipboardList,
} from "lucide-react";
import { getCompanyProposals, updateProposal } from "../../../../api/settings";
import { showToast } from "../../../../utils/showToast";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { useTranslation } from "react-i18next";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  active:   { label: "Activa",   className: "as-badge--success" },
  accepted: { label: "Aceptada", className: "as-badge--success" },
  rejected: { label: "Rechazada", className: "as-badge--neutral" },
};

const formatCurrency = (amount, currency = "CLP") => {
  if (amount == null) return "—";
  return new Intl.NumberFormat("es-CL", { style: "currency", currency }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("es-CL", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton while loading
// ─────────────────────────────────────────────────────────────────────────────

function ProposalSkeleton() {
  return (
    <div className="pt-skeleton-grid">
      {[1, 2].map((k) => (
        <div key={k} className="pt-card pt-card--skeleton">
          <div className="as-skeleton" style={{ height: 20, width: "60%", marginBottom: 12 }} />
          <div className="as-skeleton" style={{ height: 14, width: "40%", marginBottom: 8 }} />
          <div className="as-skeleton" style={{ height: 14, width: "50%", marginBottom: 8 }} />
          <div className="as-skeleton" style={{ height: 14, width: "35%" }} />
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Edit modal
// ─────────────────────────────────────────────────────────────────────────────

function EditModal({ proposal, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    companyName: proposal.companyName ?? "",
    status: proposal.status ?? "active",
    insuranceAmount: proposal.insurance?.amount ?? "",
    insuranceCurrency: proposal.insurance?.currency ?? "CLP",
    pickupMinPackages: proposal.pickupDetails?.minPackagesForFreePickup ?? "",
    pickupSurcharge: proposal.pickupDetails?.surchargePerDayForLessThanMin ?? "",
    validityStart: proposal.validityPeriod?.startDate?.slice(0, 10) ?? "",
    validityEnd: proposal.validityPeriod?.endDate?.slice(0, 10) ?? "",
    eventTitle: proposal.validityPeriod?.eventTitle ?? "",
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        companyName: form.companyName,
        status: form.status,
        insurance: {
          ...proposal.insurance,
          amount: Number(form.insuranceAmount),
          currency: form.insuranceCurrency,
        },
        pickupDetails: {
          ...proposal.pickupDetails,
          minPackagesForFreePickup: Number(form.pickupMinPackages),
          surchargePerDayForLessThanMin: Number(form.pickupSurcharge),
        },
        validityPeriod: {
          ...proposal.validityPeriod,
          startDate: form.validityStart,
          endDate: form.validityEnd,
          eventTitle: form.eventTitle,
        },
      };
      await updateProposal(proposal._id, payload);
      showToast(t("Propuesta actualizada correctamente"), true);
      onSaved({ ...proposal, ...payload });
    } catch {
      showToast(t("No se pudo actualizar la propuesta"), false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={t("Editar propuesta")}
      bodyClassName="p-0"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button variant="primary" onClick={handleSave} loading={saving} iconLeft={<Save size={16} />}>
            {saving ? t("Guardando…") : t("Guardar cambios")}
          </Button>
        </>
      }
    >
      <div className="pt-modal pt-modal--embedded">
        <div className="pt-modal__body">
          {/* Nombre */}
          <label className="pt-field">
            <span className="pt-field__label">{t("Nombre de la empresa")}</span>
            <input
              className="pt-field__input"
              value={form.companyName}
              onChange={(e) => handleChange("companyName", e.target.value)}
            />
          </label>

          {/* Estado */}
          <label className="pt-field">
            <span className="pt-field__label">{t("Estado")}</span>
            <select
              className="pt-field__input"
              value={form.status}
              onChange={(e) => handleChange("status", e.target.value)}
            >
              <option value="active">{t("Activa")}</option>
              <option value="accepted">{t("Aceptada")}</option>
              <option value="rejected">{t("Rechazada")}</option>
            </select>
          </label>

          {/* Seguro */}
          <fieldset className="pt-fieldset">
            <legend className="pt-fieldset__legend">{t("Seguro")}</legend>
            <div className="pt-field-row">
              <label className="pt-field">
                <span className="pt-field__label">{t("Monto")}</span>
                <input
                  type="number" min={0} className="pt-field__input"
                  value={form.insuranceAmount}
                  onChange={(e) => handleChange("insuranceAmount", e.target.value)}
                />
              </label>
              <label className="pt-field">
                <span className="pt-field__label">{t("Moneda")}</span>
                <input
                  className="pt-field__input"
                  value={form.insuranceCurrency}
                  onChange={(e) => handleChange("insuranceCurrency", e.target.value)}
                />
              </label>
            </div>
          </fieldset>

          {/* Retiro */}
          <fieldset className="pt-fieldset">
            <legend className="pt-fieldset__legend">{t("Detalles de retiro")}</legend>
            <div className="pt-field-row">
              <label className="pt-field">
                <span className="pt-field__label">{t("Mín. paquetes gratis")}</span>
                <input
                  type="number" min={0} className="pt-field__input"
                  value={form.pickupMinPackages}
                  onChange={(e) => handleChange("pickupMinPackages", e.target.value)}
                />
              </label>
              <label className="pt-field">
                <span className="pt-field__label">{t("Recargo por día")}</span>
                <input
                  type="number" min={0} className="pt-field__input"
                  value={form.pickupSurcharge}
                  onChange={(e) => handleChange("pickupSurcharge", e.target.value)}
                />
              </label>
            </div>
          </fieldset>

          {/* Vigencia */}
          <fieldset className="pt-fieldset">
            <legend className="pt-fieldset__legend">{t("Período de vigencia")}</legend>
            <label className="pt-field">
              <span className="pt-field__label">{t("Título del evento")}</span>
              <input
                className="pt-field__input"
                value={form.eventTitle}
                onChange={(e) => handleChange("eventTitle", e.target.value)}
              />
            </label>
            <div className="pt-field-row">
              <label className="pt-field">
                <span className="pt-field__label">{t("Inicio")}</span>
                <input
                  type="date" className="pt-field__input"
                  value={form.validityStart}
                  onChange={(e) => handleChange("validityStart", e.target.value)}
                />
              </label>
              <label className="pt-field">
                <span className="pt-field__label">{t("Fin")}</span>
                <input
                  type="date" className="pt-field__input"
                  value={form.validityEnd}
                  onChange={(e) => handleChange("validityEnd", e.target.value)}
                />
              </label>
            </div>
          </fieldset>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Proposal card
// ─────────────────────────────────────────────────────────────────────────────

function ProposalCard({ proposal, onEdit }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const status = STATUS_LABELS[proposal.status] ?? STATUS_LABELS.active;

  return (
    <article className="pt-card">
      <div className="pt-card__header">
        <div className="pt-card__title-row">
          <h3 className="pt-card__name">{proposal.companyName}</h3>
          <span className={`as-badge ${status.className}`}>{t(status.label)}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => onEdit(proposal)}
          aria-label={t("Editar propuesta de {{companyName}}", { companyName: proposal.companyName })}
          iconLeft={<Edit2 size={14} />}
        >
          {t("Editar")}
        </Button>
      </div>

      <div className="pt-card__meta">
        {proposal.insurance?.amount != null && (
          <div className="pt-card__meta-item">
            <Shield size={14} aria-hidden="true" />
            <span>{t("Seguro:")} {formatCurrency(proposal.insurance.amount, proposal.insurance.currency)}</span>
          </div>
        )}
        {proposal.pickupDetails?.minPackagesForFreePickup != null && (
          <div className="pt-card__meta-item">
            <Package size={14} aria-hidden="true" />
            <span>{t("Retiro gratis desde")} {proposal.pickupDetails.minPackagesForFreePickup} pkgs</span>
          </div>
        )}
        {proposal.validityPeriod?.startDate && (
          <div className="pt-card__meta-item">
            <Calendar size={14} aria-hidden="true" />
            <span>{formatDate(proposal.validityPeriod.startDate)} — {formatDate(proposal.validityPeriod.endDate)}</span>
          </div>
        )}
        {Array.isArray(proposal.services) && proposal.services.length > 0 && (
          <div className="pt-card__meta-item">
            <DollarSign size={14} aria-hidden="true" />
            <span>{proposal.services.length} {t("servicio")}{proposal.services.length !== 1 ? "s" : ""}</span>
          </div>
        )}
      </div>

      {Array.isArray(proposal.services) && proposal.services.length > 0 && (
        <div className="pt-card__services">
          <button
            type="button"
            className="pt-card__expand-btn"
            onClick={() => setExpanded((p) => !p)}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {expanded ? t("Ocultar servicios") : t("Ver servicios")}
          </button>
          {expanded && (
            <ul className="pt-services">
              {proposal.services.map((svc, i) => (
                <li key={i} className="pt-services__item">
                  <span className="pt-services__name">{svc.serviceName}</span>
                  <span className="pt-services__desc">{svc.description}</span>
                  <span className="pt-services__pkgs">{svc.packagesDelivered} {t("paquetes")}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ProposalsTab
// ─────────────────────────────────────────────────────────────────────────────

export default function ProposalsTab({ companyId }) {
  const { t } = useTranslation();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProposal, setEditingProposal] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    getCompanyProposals(companyId)
      .then((res) => setProposals(res.data ?? res))
      .catch(() => setError(t("No se pudieron cargar las propuestas.")))
      .finally(() => setLoading(false));
  }, [companyId]);

  const handleSaved = (updated) => {
    setProposals((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
    setEditingProposal(null);
  };

  if (loading) return <ProposalSkeleton />;

  if (error) {
    return (
      <div className="as-empty">
        <AlertCircle size={40} className="as-empty__icon" />
        <h3 className="as-empty__title">{t("Error al cargar propuestas")}</h3>
        <p className="as-empty__body">{error}</p>
      </div>
    );
  }

  if (proposals.length === 0) {
    return (
      <div className="as-empty">
        <ClipboardList size={48} className="as-empty__icon" />
        <h3 className="as-empty__title">{t("Sin propuestas configuradas")}</h3>
        <p className="as-empty__body">
          {t("Aún no tienes propuestas de valor. Contacta a soporte para crearlas.")}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="pt-skeleton-grid">
        {proposals.map((p) => (
          <ProposalCard key={p._id} proposal={p} onEdit={setEditingProposal} />
        ))}
      </div>

      {editingProposal && (
        <EditModal
          proposal={editingProposal}
          onClose={() => setEditingProposal(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
